import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ThumbDownAltTwoToneIcon from "@mui/icons-material/ThumbDownAltTwoTone";
import TuneIcon from "@mui/icons-material/Tune";
import CategoryIcon from "@mui/icons-material/Category";
import LayersIcon from "@mui/icons-material/Layers";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import BadgeIcon from "@mui/icons-material/Badge";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import QrCodeIcon from "@mui/icons-material/QrCode";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import HailTwoToneIcon from "@mui/icons-material/HailTwoTone";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PlaceIcon from "@mui/icons-material/Place";
import PaidIcon from "@mui/icons-material/Paid";
import LinkIcon from "@mui/icons-material/Link";
import ExtensionIcon from "@mui/icons-material/Extension";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import DynamicFormIcon from "@mui/icons-material/DynamicForm";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import MapIcon from "@mui/icons-material/Map";
import HubIcon from "@mui/icons-material/Hub";
import ChatIcon from "@mui/icons-material/Chat";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link, useLocation } from "react-router-dom";
import MapTwoToneIcon from "@mui/icons-material/MapTwoTone";

const SidebarNav = ({ drawerWidth, mobileOpen, onClose }) => {
  const theme = useTheme();
  const userType = localStorage.getItem("user_type");
  const [presentResourceNames, setPresentResourceNames] = useState([]);
  const [openSections, setOpenSections] = useState({
    jobs: true,
    utility: false,
    marketing: false,
    users: true,
    roles: false,
    payment: false,
    jobSeva: false,
    jyplus: false,
    support: false,
  });
  const location = useLocation();

  const isResourcePresent = () => {
    const storedData = JSON.parse(localStorage.getItem("roleData"));
    const userData = storedData?.[0];
    const resourceNames = userData?.resources?.map((resource) => resource.resource) || [];
    localStorage.setItem("resourceNames", JSON.stringify(resourceNames));
    return resourceNames;
  };

  useEffect(() => {
    const resourceNamesArray = isResourcePresent();
    setPresentResourceNames(resourceNamesArray);
  }, []);

  const hasResource = (resource) => presentResourceNames.includes(resource);

  const navSections = useMemo(() => {
    const sections = [];

    sections.push({
      title: "Dashboard",
      key: "dashboard",
      items: [
        {
          label: "Overview",
          icon: <DashboardCustomizeIcon />,
          to: "/newdashboard",
          disabled: !hasResource("Dashboard"),
        },
      ],
    });

    if (
      presentResourceNames.some((resource) =>
        [
          "All Jobs",
          "Rejected Jobs",
          "Approved Jobs",
          "Job Category",
          "Job Title",
          "Pending Jobs",
        ].includes(resource)
      )
    ) {
      sections.push({
        title: "Jobs",
        key: "jobs",
        items: [
          {
            label: "All Jobs",
            icon: <WorkOutlineIcon />,
            to: "/all-jobs",
            disabled: !hasResource("All Jobs"),
          },
          {
            label: "Rejected Jobs",
            icon: <ThumbDownAltTwoToneIcon />,
            to: "/rejected-jobs",
            disabled: !hasResource("Rejected Jobs"),
          },
        ],
        nested: [
          {
            label: "Job Settings",
            icon: <TuneIcon />,
            items: [
              {
                label: "Job Categories",
                icon: <CategoryIcon />,
                to: "/job-categories",
                disabled: !hasResource("Job Category"),
              },
              {
                label: "Job Sectors",
                icon: <LayersIcon />,
                to: "/job-sectors",
                disabled: !hasResource("Job Sectors"),
              },
              {
                label: "Job Occupations",
                icon: <WorkspacesIcon />,
                to: "/job-occupations",
                disabled: !hasResource("Job Occupations"),
              },
              {
                label: "Job Roles",
                icon: <BadgeIcon />,
                to: "/job-roles",
                disabled: !hasResource("Job Roles"),
              },
            ],
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Plus"].includes(resource))) {
      sections.push({
        title: "JY Plus",
        key: "jyplus",
        items: [
          {
            label: "Form Builder",
            icon: <DynamicFormIcon />,
            to: "/form-builder",
            disabled: !hasResource("Plus"),
          },
          {
            label: "Plus Candidates",
            icon: <PersonAddAltIcon />,
            to: "/plus/candidates",
            disabled: !hasResource("Plus"),
          },
          {
            label: "Regions",
            icon: <MapIcon />,
            to: "/regions",
            disabled: !hasResource("Plus"),
          },
          {
            label: "Region Map View",
            icon: <MapTwoToneIcon />,
            to: "/region-map-view",
            disabled: !hasResource("Plus"),
          },
          {
            label: "Region Mapping",
            icon: <HubIcon />,
            to: "/region-mapping",
            disabled: !hasResource("Plus"),
          },
          {
            label: "SNF Dashboard",
            icon: <QueryStatsIcon />,
            to: "/snf",
            disabled: !hasResource("Plus"),
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Utility"].includes(resource))) {
      sections.push({
        title: "Utility",
        key: "utility",
        items: [
          {
            label: "URL Shortener",
            icon: <LinkIcon />,
            to: "/url-shortener",
            disabled: !hasResource("URL Shortener"),
          },
          {
            label: "HC Addons",
            icon: <ExtensionIcon />,
            to: "/addon",
            disabled: !hasResource("HC-Addon"),
          },
          {
            label: "Plans",
            icon: <LocalOfferIcon />,
            to: "/plans",
            disabled: !hasResource("HC-Plans"),
          },
        ],
      });
    }

    if (
      presentResourceNames.some((resource) =>
        ["Qr stats", "Manage Qr", "Generate Qr"].includes(resource)
      )
    ) {
      sections.push({
        title: "QR",
        key: "qr",
        items: [
          {
            label: "Stats",
            icon: <QueryStatsIcon />,
            to: "#",
            disabled: !hasResource("Qr stats"),
          },
          {
            label: "Manage QR",
            icon: <QrCodeScannerTwoToneIcon />,
            to: "#",
            disabled: !hasResource("Manage Qr"),
          },
          {
            label: "Generate QR",
            icon: <QrCodeIcon />,
            to: "#",
            disabled: !hasResource("Generate Qr"),
          },
        ],
      });
    }

    if (
      presentResourceNames.some((resource) =>
        ["Marketing", "Whatsapp Campaign", "Email Campaign", "Notifcation Campaign", "Banner Campaign", "SEO", "jobLocationContent"].includes(resource)
      )
    ) {
      sections.push({
        title: "Marketing",
        key: "marketing",
        items: [
          {
            label: "WhatsApp Campaign",
            icon: <WhatsAppIcon />,
            to: "/whatsapp-campaign",
            disabled: !hasResource("Whatsapp Campaign"),
          },
          {
            label: "Email Campaign",
            icon: <EmailIcon />,
            to: "/email-campaign",
            disabled: !hasResource("Email Campaign"),
          },
          {
            label: "Notifications",
            icon: <NotificationsActiveIcon />,
            to: "/notifications",
            disabled: !hasResource("Notifcation Campaign"),
          },
          {
            label: "SEO",
            icon: <TravelExploreIcon />,
            to: "/seo",
            disabled: !hasResource("SEO"),
          },
          {
            label: "Jobs By Location",
            icon: <PlaceIcon />,
            to: "/jobs-by-locations",
            disabled: !hasResource("jobLocationContent"),
          },
        ],
      });
    }

    if (
      presentResourceNames.some((resource) =>
        ["Candidate", "Employer", "Partner", "Job Sahayak", "Recruiter"].includes(resource)
      )
    ) {
      sections.push({
        title: "Users",
        key: "users",
        items: [
          {
            label: "Candidate",
            icon: <PersonOutlineIcon />,
            to: "/candidate",
            disabled: !hasResource("Candidate"),
          },
          {
            label: "Employer",
            icon: <BusinessIcon />,
            to: "/employer",
            disabled: !hasResource("Employer"),
          },
          {
            label: "Recruiter",
            icon: <SupportAgentIcon />,
            to: "/recruiter",
            disabled: !hasResource("Recruiter"),
          },
          {
            label: "JobSahayak",
            icon: <HailTwoToneIcon />,
            to: "/jobsahayak",
            disabled: !hasResource("Job Sahayak"),
          },
          {
            label: "Partner",
            icon: <PersonAddAltIcon />,
            to: "/partner",
            disabled: !hasResource("Partner"),
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Manage Roles", "Config Roles"].includes(resource))) {
      sections.push({
        title: "Roles & Permissions",
        key: "roles",
        items: [
          {
            label: "Config Roles",
            icon: <AdminPanelSettingsIcon />,
            to: "/config-roles",
            disabled: !hasResource("Config Roles"),
          },
          {
            label: "Manage Roles",
            icon: <ManageAccountsIcon />,
            to: "/manage-roles",
            disabled: !hasResource("Manage Roles"),
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Payment Details"].includes(resource))) {
      sections.push({
        title: "Payment",
        key: "payment",
        items: [
          {
            label: "Transactions",
            icon: <PaidIcon />,
            to: "/transactions",
            disabled: !hasResource("Payment Details"),
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Reports", "Payment Details"].includes(resource))) {
      sections.push({
        title: "Reports",
        key: "reports",
        items: [
          {
            label: "Invoices",
            icon: <ReceiptLongIcon />,
            to: "/reports/invoices",
            disabled: !hasResource("Reports") && !hasResource("Payment Details"),
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Jobseva"].includes(resource))) {
      sections.push({
        title: "JobSeva",
        key: "jobseva",
        items: [
          {
            label: "Jobs",
            icon: <WorkOutlineIcon />,
            to: "#",
            disabled: true,
          },
          {
            label: "JWP Task",
            icon: <AssignmentIcon />,
            to: "#",
            disabled: true,
          },
          {
            label: "Announcement",
            icon: <NotificationsActiveIcon />,
            to: "#",
            disabled: true,
          },
          {
            label: "JWP",
            icon: <HandshakeIcon />,
            to: "#",
            disabled: true,
          },
          {
            label: "JSK",
            icon: <AddBusinessIcon />,
            to: "#",
            disabled: true,
          },
        ],
      });
    }

    if (presentResourceNames.some((resource) => ["Contact Queries"].includes(resource))) {
      sections.push({
        title: "Support",
        key: "support",
        items: [
          {
            label: "Contact Us",
            icon: <ContactSupportIcon />,
            to: "/contact-us-queries",
            disabled: !hasResource("Contact Queries"),
          },
        ],
      });
    }



    if (presentResourceNames.some((resource) => ["Rasa"].includes(resource))) {
      sections.push({
        title: "Rasa",
        key: "rasa",
        items: [
          {
            label: "Chats",
            icon: <ChatIcon />,
            to: "/chats",
            disabled: !hasResource("Rasa"),
          },
        ],
      });
    }

    return sections;
  }, [presentResourceNames]);

  const handleSectionToggle = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255, 140, 0, 0.12)" : "rgba(122, 75, 117, 0.12)",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: theme.palette.mode === "dark" ? theme.palette.primary.main : "#7a4b75",
              color: theme.palette.mode === "dark" ? "#1a1a1a" : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            JY
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
              JobsYahan
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Admin Console
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ px: 2, flexGrow: 1, overflowY: "auto" }}>
        {navSections.map((section) => (
          <Box key={section.key} sx={{ mb: 1.5 }}>
            <ListItemButton
              sx={{
                borderRadius: 3,
                mb: 0.5,
                px: 1.5,
                py: 1,
                bgcolor:
                  openSections[section.key]
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 140, 0, 0.18)"
                      : "rgba(122, 75, 117, 0.12)"
                    : "transparent",
              }}
              onClick={() => handleSectionToggle(section.key)}
            >
              <ListItemText
                primary={
                  <Typography variant="overline" sx={{ letterSpacing: 1, fontWeight: 700, color: "text.secondary" }}>
                    {section.title}
                  </Typography>
                }
              />
              {openSections[section.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={openSections[section.key]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {section.items.map((item) => {
                  const isActive = item.to !== "#" && location.pathname === item.to;
                  const content = (
                    <ListItemButton
                      key={item.label}
                      sx={{
                        pl: 2,
                        pr: 1.5,
                        py: 1,
                        borderRadius: 3,
                        mb: 0.5,
                        bgcolor: isActive
                          ? theme.palette.mode === "dark"
                            ? "rgba(255, 140, 0, 0.22)"
                            : "#7a4b75"
                          : "transparent",
                        color: isActive
                          ? theme.palette.mode === "dark"
                            ? theme.palette.text.primary
                            : "#fff"
                          : "text.primary",
                        opacity: item.disabled ? 0.5 : 1,
                        "&:hover": {
                          bgcolor: isActive
                            ? theme.palette.mode === "dark"
                              ? "rgba(255, 140, 0, 0.28)"
                              : "#6b3f67"
                            : theme.palette.mode === "dark"
                              ? "rgba(255, 140, 0, 0.12)"
                              : "rgba(122, 75, 117, 0.08)",
                        },
                      }}
                      disabled={item.disabled}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: isActive
                            ? theme.palette.mode === "dark"
                              ? theme.palette.text.primary
                              : "#fff"
                            : "text.secondary",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500 }}
                      />
                    </ListItemButton>
                  );

                  return item.to === "#" ? (
                    <Box key={item.label}>{content}</Box>
                  ) : (
                    <Box key={item.label} component={Link} to={item.to} sx={{ textDecoration: "none", color: "inherit" }}>
                      {content}
                    </Box>
                  );
                })}
                {section.nested?.map((nested) => (
                  <Box key={nested.label} sx={{ ml: 1 }}>
                    <ListItemButton
                      sx={{
                        borderRadius: 3,
                        mb: 0.5,
                        pl: 2,
                        py: 1,
                        color: "text.primary",
                        "&:hover": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 140, 0, 0.12)"
                              : "rgba(122, 75, 117, 0.08)",
                        },
                      }}
                      onClick={() => handleSectionToggle(nested.label)}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: "text.secondary" }}>{nested.icon}</ListItemIcon>
                      <ListItemText primary={nested.label} />
                      {openSections[nested.label] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItemButton>

                    <Collapse in={openSections[nested.label]} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {nested.items.map((child) => {
                          const isActive = child.to !== "#" && location.pathname === child.to;
                          return (
                            <Box
                              key={child.label}
                              component={child.to === "#" ? "div" : Link}
                              to={child.to === "#" ? undefined : child.to}
                              sx={{ textDecoration: "none", color: "inherit" }}
                            >
                              <ListItemButton
                                sx={{
                                  pl: 4,
                                  py: 0.75,
                                  borderRadius: 3,
                                  mb: 0.5,
                                  bgcolor: isActive
                                    ? theme.palette.mode === "dark"
                                      ? "rgba(255, 140, 0, 0.22)"
                                      : "#7a4b75"
                                    : "transparent",
                                  color: isActive
                                    ? theme.palette.mode === "dark"
                                      ? theme.palette.text.primary
                                      : "#fff"
                                    : "text.primary",
                                  opacity: child.disabled ? 0.5 : 1,
                                  "&:hover": {
                                    bgcolor: isActive
                                      ? theme.palette.mode === "dark"
                                        ? "rgba(255, 140, 0, 0.28)"
                                        : "#6b3f67"
                                      : theme.palette.mode === "dark"
                                        ? "rgba(255, 140, 0, 0.12)"
                                        : "rgba(122, 75, 117, 0.08)",
                                  },
                                }}
                                disabled={child.disabled}
                              >
                                <ListItemText
                                  primary={child.label}
                                  primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 500 }}
                                />
                              </ListItemButton>
                            </Box>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (userType === "jobsyahan_shayak") {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "none",
            bgcolor: theme.palette.mode === "dark" ? "#141112" : "#fbf9fc",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "none",
            bgcolor: theme.palette.mode === "dark" ? "#141112" : "#fbf9fc",
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "none",
            bgcolor: theme.palette.mode === "dark" ? "#141112" : "#fbf9fc",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default SidebarNav;
